import { NavLink, useLocation } from "react-router-dom";
import {
  Home,
  LayoutGrid,
  History,
  FileText,
  Package,
  Settings,
  Clock,
  TrendingUp,
  MessageSquare,
  Users,
  ChevronDown,
  Circle,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { BrandWordmark } from "@/components/brand-wordmark";
import { TERMS } from "@tasty/shared";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

interface SidebarProps {
  restaurantName?: string;
  restaurantStatus?: string;
  onStatusChange?: (active: boolean) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

const navItems = [
  { path: "/", label: "Home", icon: Home },
  { path: "/orders", label: "Orders", icon: LayoutGrid },
  { path: "/order-history", label: "Historie", icon: History },
  { path: "/analytics", label: "Analytics", icon: TrendingUp, disabled: true },
  { path: "/insights", label: "Insights", icon: MessageSquare, disabled: true },
  { path: "/menu", label: TERMS.catalog, icon: Package },
  { path: "/invoices", label: "Rechnungen", icon: FileText },
  { path: "/business-hours", label: "Holiday Hours", icon: Clock },
  { path: "/users", label: "Users", icon: Users, disabled: true },
  { path: "/settings", label: "Settings", icon: Settings },
];

export function Sidebar({ 
  restaurantName, 
  restaurantStatus, 
  onStatusChange, 
  isCollapsed, 
  onToggleCollapse,
  isMobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  const location = useLocation();
  const isActive = restaurantStatus === "ACTIVE";

  const handleNavClick = () => {
    if (isMobileOpen) {
      onMobileClose?.();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm md:hidden" 
          onClick={onMobileClose}
        />
      )}

      <aside className={cn(
        "fixed left-0 top-0 bottom-0 z-50 w-64 bg-background border-r border-border flex flex-col pt-4 transition-transform duration-300 md:top-14",
        isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
        isCollapsed && "md:w-20"
      )}>
        {/* Mobile Header (only visible on mobile) */}
        <div className="flex items-center justify-between px-4 mb-4 md:hidden">
          <BrandWordmark className="font-black text-lg tracking-tighter" />
          <Button variant="ghost" size="icon" onClick={onMobileClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Top: All Stores Selector (Enterprise Placeholder) */}
        <div className="px-4 mb-6">
          <div className="group flex flex-col gap-1 cursor-not-allowed opacity-60">
            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground ml-1">Enterprise</span>
            <div className="flex items-center justify-between p-2 rounded-lg border border-muted-foreground/10 bg-muted/5 group-hover:bg-muted/10 transition-all">
              <span className="text-xs font-bold truncate">Alle Filialen</span>
              <ChevronDown className="w-3 h-3" />
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto scrollbar-hide">
          {navItems.map((item) => {
            const isCurrentPage = location.pathname === item.path || 
                                 (item.path !== "/" && location.pathname.startsWith(item.path));
            const Icon = item.icon;

            if (item.disabled) {
              return (
                <div 
                  key={item.path}
                  className="flex items-center gap-3 px-3 py-2 text-muted-foreground/30 cursor-not-allowed group relative"
                >
                  <Icon className="w-[18px] h-[18px] shrink-0" />
                  <span className="text-[11px] font-bold uppercase tracking-wide">{item.label}</span>
                  <Badge variant="outline" className="ml-auto text-[7px] h-3 px-1 border-muted-foreground/10 text-muted-foreground/30 uppercase font-black">Soon</Badge>
                </div>
              );
            }

            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 transition-all duration-200 group relative",
                  isCurrentPage
                    ? "bg-primary/5 text-primary"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                )}
              >
                {isCurrentPage && (
                  <div className="absolute left-0 w-1 h-4 bg-primary rounded-full" />
                )}
                <Icon className={cn(
                  "w-[18px] h-[18px] shrink-0 transition-colors",
                  isCurrentPage ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
                )} />
                <span className={cn(
                  "text-[11px] font-bold uppercase tracking-wide",
                  isCurrentPage ? "text-primary" : ""
                )}>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom Section: Restaurant Status & Info */}
        <div className="p-4 space-y-3 border-t border-border bg-muted/5">
          <div className={cn(
            "flex items-center justify-between p-3 rounded-xl border transition-all shadow-sm",
            isActive ? "bg-success/10 border-success/30" : "bg-muted/50 border-muted-foreground/10 opacity-80"
          )}>
            <div className="flex items-center gap-2">
              <Circle className={cn(
                "w-2 h-2 fill-current",
                isActive ? "text-success animate-pulse-subtle" : "text-muted-foreground"
              )} />
              <span className={cn(
                "text-[10px] font-black uppercase tracking-widest",
                isActive ? "text-success" : "text-muted-foreground"
              )}>{isActive ? "Online" : "Offline"}</span>
            </div>
            <Switch 
              checked={isActive} 
              onCheckedChange={onStatusChange}
              className="scale-75 origin-right"
            />
          </div>

          <div className="p-3 bg-card border border-muted-foreground/10 rounded-xl shadow-sm">
            <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/60 mb-1">Store Context</p>
            <p className="text-[11px] font-bold truncate leading-tight">{restaurantName || "Select Store"}</p>
          </div>
        </div>
      </aside>
    </>
  );
}
